import React, { useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import { Box, Button, Card, Typography } from "@curves/ui-base";
import { Grid } from "@mui/material";
import { getCheckedBooleanValue, refIsRequiredError } from "@util/utils";
import useMembershipQuitForm from "@icoach/documents/membership/quit/hook/useMembershipQuitForm";
import useShowPDF from "@util/hook/useShowPDF";
import useMessageDialog from "@util/hook/useMessageDialog";
import { CLUB_DOCUMENTS_ROUTER } from "@icoach/router/routerPath";
import { handleInitialFormData } from "@icoach/documents/membership/quit/staticData";
import { MembershipQuitFormProvider } from "@icoach/documents/membership/quit/MembershipQuitFormContext";
import MembershipQuitFormInfoSection104 from "@icoach/documents/membership/quit/version104/components/MembershipQuitFormInfoSection104";
import MembershipQuitRangeFeeSection104 from "@icoach/documents/membership/quit/version104/components/MembershipQuitRangeFeeSection104";
import MembershipQuitSettlementSection104 from "@icoach/documents/membership/quit/version104/components/MembershipQuitSettlementSection104";
import MembershipQuitNoticeSection104 from "@icoach/documents/membership/quit/version104/components/MembershipQuitNoticeSection104";

const MembershipQuitForm104 = ({ sourceData, peopleType, referenceID, isReadOnly, dialogRef, focusModeRef }) => {
	const { resource, membership, hasDeviceOrder = false, orderType, ...other } = sourceData || {};
	const history = useHistory();
	const showPDF = useShowPDF();
	const setMessageDialog = useMessageDialog();
	const formRef = useRef({});
	const rentCabinetConfirmRef = useRef(null); // 置物櫃確認，後端不記
	const deviceConfirmRef = useRef(null); // 裝置確認，後端不記
	const returnHistoryPath = CLUB_DOCUMENTS_ROUTER.QUIT.GET_HISTORY_PATH(peopleType, referenceID);

	const navigateToCompletedContract = ({ pdfPath, targetPath }) => {
		showPDF({ open: true, pdfUrl: pdfPath });
		history.push(targetPath);
	};

	const handleSubmitFormSuccess = ({ completedPath, contractID, deviceOrderCompletedPath, canRefundAll }) => {
		const readonlyPath = CLUB_DOCUMENTS_ROUTER.QUIT.GET_READONLY_PATH(peopleType, referenceID, contractID);
		let pdfs = [completedPath];
		if (deviceOrderCompletedPath) pdfs.push(deviceOrderCompletedPath);
		if (canRefundAll) {
			navigateToCompletedContract({ pdfPath: pdfs, targetPath: readonlyPath });
		} else {
			setMessageDialog({
				open: true,
				title: "提醒",
				MsgComp: () => (
					<React.Fragment>
						<Typography>若有費用需退回會員，不是在本系統處理的批次扣款與發票開立，請特案處理唷！</Typography>
						<Typography>備註：</Typography>
						<Box component="ol">
							<Box component="li">金流：請採現金退款或填寫紙本刷退申請書向銀行刷退。</Box>
							<Box component="li">發票：請至綠界網頁後台執行發票作廢或折讓。</Box>
						</Box>
					</React.Fragment>
				),
				onConfirm: () => navigateToCompletedContract({ pdfPath: pdfs, targetPath: readonlyPath }),
			});
		}
	};
	const handleSaveDraftSuccess = () => {
		history.push(returnHistoryPath);
	};

	const formAdapter = {
		getFormData: () => {
			return Object.keys(formRef.current).reduce((params, key) => {
				let value = formRef.current[key]?.getResult();
				if (Array.isArray(value)) value = getCheckedBooleanValue(value); // CheckboxField 會回傳陣列d
				return {
					...params,
					[key]: value,
				};
			}, {});
		},
		validateForm: () => {
			return !refIsRequiredError(rentCabinetConfirmRef, formRef, deviceConfirmRef);
		},
	};

	const { formData, updateField, validationErrors, onSubmitForm, onSaveDraft, manualTriggerRangeFee } = useMembershipQuitForm({
		formAdapter,
		initFormData: handleInitialFormData(other),
		onSubmitSuccess: handleSubmitFormSuccess,
		onSavaDraftSuccess: handleSaveDraftSuccess,
	});

	const handleSubmitForm = () => {
		focusModeRef.current.checkAndExitFocusMode(onSubmitForm);
	};

	const handleSaveDraft = () => {
		focusModeRef.current.checkAndExitFocusMode(onSaveDraft);
	};

	return (
		<MembershipQuitFormProvider
			value={{
				formData,
				formRef,
				validationErrors,
				updateField,
				dialogRef,
				manualTriggerRangeFee,
				isReadOnly,
			}}
		>
			<Card>
				<Card.CardHeader title="填寫表單" className="bg-light text-center" />
				<Card.CardContent className="px-4 py-5">
					<MembershipQuitFormInfoSection104 resource={resource} rentCabinetConfirmRef={rentCabinetConfirmRef} />
				</Card.CardContent>
				<Card.CardHeader title="費用計算" className="bg-light text-center" />
				<Card.CardContent className="px-4 py-5">
					<Grid container spacing={3}>
						<MembershipQuitRangeFeeSection104 sourceData={sourceData} />
						<MembershipQuitSettlementSection104 />
						<MembershipQuitNoticeSection104 hasDeviceOrder={hasDeviceOrder} deviceConfirmRef={deviceConfirmRef} />
					</Grid>
				</Card.CardContent>
				<Card.CardContent>
					{!isReadOnly ? (
						<Box className="btn-group text-center my-2">
							{!formData?.isCompleteContract && (
								<Button name="temporary" variant="outlined" color="secondary" className="btn-minWidth" onClick={handleSaveDraft}>
									暫存檔案
								</Button>
							)}
							<Button name="create" variant="contained" color="secondary" className="btn-minWidth" onClick={handleSubmitForm}>
								確認送出
							</Button>
						</Box>
					) : (
						<Box className="btn-group text-center my-1">
							<Button variant="contained" color="secondary" className="btn-minWidth" component={Link} to={returnHistoryPath}>
								返回歷史紀錄
							</Button>
						</Box>
					)}
					<Typography variant="body2" className="text-center mb-0 px-2 font-color-3 my-4">
						{formData?.contractVersionText}
					</Typography>
				</Card.CardContent>
			</Card>
		</MembershipQuitFormProvider>
	);
};

export default MembershipQuitForm104;
